import React, { useState, useEffect } from 'react';
import { userService } from '../../services/userService';

const diasSemana = [
  { key: '1', label: 'Lunes' },
  { key: '2', label: 'Martes' },
  { key: '3', label: 'Miércoles' },
  { key: '4', label: 'Jueves' },
  { key: '5', label: 'Viernes' },
  { key: '6', label: 'Sábado' },
  { key: '7', label: 'Domingo' },
];

export default function EstablecimientoPerfilForm({ user, onUpdated }) {
  const [form, setForm] = useState({
    nombre_negocio: user?.nombre_negocio || '',
    direccion: user?.direccion || '',
    telefono: user?.telefono || '',
    imagenes: user?.imagenes || [],
    horarios_disponibles: user?.horarios_disponibles || {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Puedes cargar los datos actuales del usuario aquí si tienes un endpoint
    // Ejemplo: userService.getProfile().then(...)
  }, []);

  // IMAGENES: Manejo de archivos
  const handleImagenesChange = e => {
    const files = Array.from(e.target.files);
    // Convertir a base64 para previsualización y envío
    Promise.all(files.map(file => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    })).then(imagesBase64 => {
      setForm(prev => ({ ...prev, imagenes: imagesBase64 }));
    });
  };

  // HORARIOS: Manejo dinámico
  const handleHorarioChange = (dia, idx, field, value) => {
    setForm(prev => {
      const horarios = { ...prev.horarios_disponibles };
      if (!horarios[dia]) horarios[dia] = [{ hora_inicio: '', hora_fin: '' }];
      horarios[dia][idx][field] = value;
      return { ...prev, horarios_disponibles: horarios };
    });
  };

  const addHorario = dia => {
    setForm(prev => {
      const horarios = { ...prev.horarios_disponibles };
      if (!horarios[dia]) horarios[dia] = [];
      horarios[dia].push({ hora_inicio: '', hora_fin: '' });
      return { ...prev, horarios_disponibles: horarios };
    });
  };

  const removeHorario = (dia, idx) => {
    setForm(prev => {
      const horarios = { ...prev.horarios_disponibles };
      horarios[dia].splice(idx, 1);
      return { ...prev, horarios_disponibles: horarios };
    });
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...form,
        // imagenes ya es array base64
        horarios_disponibles: form.horarios_disponibles,
      };
      await userService.updatePerfil(payload);
      setLoading(false);
      setSuccess('Perfil actualizado correctamente');
      if (onUpdated) onUpdated();
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message || 'Error al actualizar el perfil');
    }
  };

  // AUTOFILL HORARIOS: Rellenar todos los días con el mismo horario
  const autofillHorarios = () => {
    // Busca el primer día con al menos un horario
    const primerDia = diasSemana.find(dia => (form.horarios_disponibles[dia.key] && form.horarios_disponibles[dia.key].length > 0));
    if (!primerDia) return;
    const horarioBase = form.horarios_disponibles[primerDia.key];
    const nuevosHorarios = {};
    diasSemana.forEach(dia => {
      nuevosHorarios[dia.key] = horarioBase.map(h => ({ ...h }));
    });
    setForm(prev => ({ ...prev, horarios_disponibles: nuevosHorarios }));
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-md mx-auto p-4 bg-white rounded shadow">
      <h2 className="text-xl font-bold mb-4">Editar Perfil del Establecimiento</h2>
      <input name="nombre_negocio" value={form.nombre_negocio} onChange={handleChange} placeholder="Nombre del negocio" className="mb-2 w-full p-2 border" />
      <input name="direccion" value={form.direccion} onChange={handleChange} placeholder="Dirección" className="mb-2 w-full p-2 border" />
      <input name="telefono" value={form.telefono} onChange={handleChange} placeholder="Teléfono" className="mb-2 w-full p-2 border" />
      {/* Campo para subir imágenes */}
      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">Imágenes</label>
        <input type="file" multiple accept="image/*" onChange={handleImagenesChange} className="w-full p-2 border" />
        <div className="flex flex-wrap mt-2">
          {form.imagenes && form.imagenes.map((img, i) => (
            <img key={i} src={img} alt="preview" className="h-16 w-16 object-cover rounded mr-2 mb-2" />
          ))}
        </div>
        <small className="text-gray-500">Puedes subir varias imágenes desde tu dispositivo.</small>
      </div>
      {/* Campo para horarios dinámicos */}
      <div className="mb-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">Horarios Disponibles</label>
        <button type="button" onClick={autofillHorarios} className="mb-2 bg-indigo-500 text-white px-2 py-1 rounded text-xs">Rellenar todos los días con el mismo horario del primer día</button>
        {diasSemana.map(dia => (
          <div key={dia.key} className="mb-2">
            <span className="font-semibold text-gray-700">{dia.label}</span>
            {(form.horarios_disponibles[dia.key] || []).map((horario, idx) => (
              <div key={idx} className="flex items-center gap-2 mt-1">
                <input
                  type="time"
                  value={horario.hora_inicio}
                  onChange={e => handleHorarioChange(dia.key, idx, 'hora_inicio', e.target.value)}
                  className="p-1 border rounded"
                  required
                />
                <span>a</span>
                <input
                  type="time"
                  value={horario.hora_fin}
                  onChange={e => handleHorarioChange(dia.key, idx, 'hora_fin', e.target.value)}
                  className="p-1 border rounded"
                  required
                />
                <button type="button" onClick={() => removeHorario(dia.key, idx)} className="text-red-500 ml-2">Eliminar</button>
              </div>
            ))}
            <button type="button" onClick={() => addHorario(dia.key)} className="text-blue-600 text-xs mt-1">+ Agregar horario</button>
          </div>
        ))}
        <small className="text-gray-500">Agrega los horarios disponibles para cada día o usa el botón para copiar el horario del primer día.</small>
      </div>
      {error && <div className="text-red-500 mb-2">{error}</div>}
      {success && <div className="text-green-500 mb-2">{success}</div>}
      <button type="submit" disabled={loading} className="bg-blue-600 text-white px-4 py-2 rounded">
        {loading ? 'Guardando...' : 'Guardar cambios'}
      </button>
    </form>
  );
}
